/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React, { Suspense } from 'react'
import { css, jsx } from '@emotion/react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import useDocument from '@globo/react/useDocument.js'
import { useTranslation } from '@multilocale/react/index.js'
import IconBack from '@stiloso/icons/IconBack.js'
import IconClose from '@stiloso/icons/IconClose.js'
import Spinner from '@stiloso/components/Spinner.js'

export const paths = ['/documents/:documentId']

const Documents = () => {
  const { documentId } = useParams()

  let { data: document } = useDocument(documentId, {
    fields: ['_id', 'title', 'content'],
  })

  return (
    <div
      css={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
    >
      <h1 css={{ paddingBottom: 8 }}>{document.title}</h1>
      <div dangerouslySetInnerHTML={{ __html: document.content }} />
    </div>
  )
}

const DocumentPageWrapper = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const closeWidget = () => {
    window?.parent?.postMessage({ type: 'closeChat' }, '*')
  }

  const navigateToDocuments = () => {
    navigate('/documents')
  }

  return (
    <div
      css={{
        background: 'white',
        borderRadius: 16,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          display: 'flex',
          background: 'var(--color-primary)',
          color: 'white',
          fill: 'white',
          width: '100%',
          position: 'sticky',
          top: 0,
        }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              aria-label="Documents"
              onClick={navigateToDocuments}
              css={{
                background: 'transparent',
                border: 'none',
                padding: 16,
                cursor: 'pointer',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
            >
              <IconBack css={{ width: 24, height: 24 }} />
            </button>
            <Link
              css={{
                color: 'white',
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                cursor: 'pointer',
                height: 32,
                borderRadius: 24,
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
              to="/messages"
            >
              {t('Chat')}
            </Link>
            <Link
              css={{
                color: 'white',
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                cursor: 'pointer',
                height: 32,
                borderRadius: 24,
                background: 'var(--color-primary-dark)',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
              to="/documents"
            >
              {t('Docs')}
            </Link>
            <button
              type="button"
              aria-label="Close"
              onClick={closeWidget}
              css={{
                background: 'transparent',
                border: 'none',
                padding: 16,
                cursor: 'pointer',
                ':hover, :focus-visible': {
                  background: 'var(--color-hover)',
                },
                ':active': {
                  background: 'var(--color-active)',
                },
              }}
            >
              <IconClose css={{ width: 24, height: 24 }} />
            </button>
          </div>
        </div>
      </div>
      <Suspense fallback={<Spinner />}>
        <Documents />
      </Suspense>
    </div>
  )
}

export default DocumentPageWrapper
